import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, IconButton,
  Box, Typography, Grid, Paper, TextField, InputAdornment, Accordion,
  AccordionSummary, AccordionDetails, List, ListItem, ListItemText,
  Checkbox, Divider, ListItemIcon, Chip, Stack, FormControlLabel,
  useTheme, Badge, Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  Business as BusinessIcon,
  Monitor as MonitorIcon,
  Scale as ScaleIcon,
  WebAsset as WebAssetIcon,
  Dns as DnsIcon,
  Widgets as WidgetsIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  DoneAll as DoneAllIcon,
  RadioButtonUnchecked as UncheckedIcon
} from '@mui/icons-material';

// --- COSTANTI ---
const DEVICE_TYPES = [
  { id: 1, description: 'Monitor', icon: <MonitorIcon /> },
  { id: 3, description: 'Bilancia', icon: <ScaleIcon /> },
  { id: 2, description: 'Totem', icon: <WebAssetIcon /> },
];

// --- COMPONENTI INTERNI ---

// Stat Card (Fissa, non collassa)
const StatCard = ({ icon, count, label, active }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        px: 2,
        minWidth: 90, 
        height: 50, // Altezza fissa per consistenza
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 2,
        border: '1px solid',
        borderColor: active ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider,
        bgcolor: active ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
        flexShrink: 0, // CRUCIALE: Impedisce lo schiacciamento
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
         <Box sx={{ color: active ? 'primary.main' : 'text.secondary', display: 'flex', '& svg': { fontSize: '1rem'} }}>
             {icon}
         </Box>
         <Typography variant="h6" fontWeight={800} color={active ? 'primary.main' : 'text.primary'} sx={{ fontSize: '1rem', lineHeight: 1 }}>
             {count}
         </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.6rem', textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Paper>
  );
};

// Accordion Custom
const FilterAccordion = ({ icon, title, children, defaultExpanded = false }) => {
  const theme = useTheme();
  return (
    <Accordion 
      defaultExpanded={defaultExpanded} 
      disableGutters 
      elevation={0}
      sx={{ 
        '&:before': { display: 'none' }, 
        bgcolor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        mb: 0.5
      }}
    >
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ 
          minHeight: 40, 
          borderRadius: 2,
          px: 1.5,
          '&:hover': { bgcolor: alpha(theme.palette.action.active, 0.04) },
          '&.Mui-expanded': { minHeight: 40 },
          '& .MuiAccordionSummary-content': { my: 1 }
        }}
      >
        <Box display="flex" alignItems="center" color="text.secondary" sx={{ width: '100%' }}>
          <Box sx={{ mr: 1.5, display: 'flex', color: theme.palette.action.active }}>
             {React.cloneElement(icon, { fontSize: 'small' })}
          </Box>
          <Typography variant="body2" fontWeight={700} color="text.primary">{title}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pt: 0.5, pl: 1 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

// --- COMPONENTE PRINCIPALE ---

export default function Playlist({ configuration, open, onClose, onSave, selectedCount, selectedItems = [], mediaMode='MEDIA'}) {
  const theme = useTheme();
  
  // Setup Configurazione
  const localConfiguration = JSON.parse(JSON.stringify(configuration));
  localConfiguration["deviceTypes"] = DEVICE_TYPES;
  
  if (mediaMode === 'EXTRA') {
    localConfiguration.deviceTypes = localConfiguration.deviceTypes.filter(d => d.id === 2);
    localConfiguration.devices = localConfiguration.devices.filter(d => d.dtype === 2);
  }

  const [filters, setFilters] = useState({
    storeGroups: [],
    stores: [],
    deviceGroups: [],
    deviceTypes: [],
    searchTerm: '',
  });

  const [selectedDevices, setSelectedDevices] = useState([]);

  // --- MEMOS ---
  const availableDevices = useMemo(() => {
    let devices = localConfiguration.devices;
    
    if (filters.storeGroups.length > 0) {
      const storesInGroup = localConfiguration.stores
        .filter(s => filters.storeGroups.includes(s.store_group.id))
        .map(s => s.id);
      devices = devices.filter(d => storesInGroup.includes(d.store));
    }
    if (filters.stores.length > 0) {
      devices = devices.filter(d => filters.stores.includes(d.store));
    }
    if (filters.deviceGroups.length > 0) {
      devices = devices.filter(d => filters.deviceGroups.includes(d.dgroup));
    }
    if (filters.deviceTypes.length > 0) {
      devices = devices.filter(d => filters.deviceTypes.includes(d.dtype));
    }
    if (filters.searchTerm) {
      const lowerCaseSearch = filters.searchTerm.toLowerCase();
      const storesByName = localConfiguration.stores
        .filter(s => s.description.toLowerCase().includes(lowerCaseSearch))
        .map(s => s.id);
      devices = devices.filter(d =>
        d.description.toLowerCase().includes(lowerCaseSearch) || storesByName.includes(d.store)
      );
    }
    return devices;
  }, [filters, localConfiguration.devices, localConfiguration.stores]);

  const deviceTypeBreakdown = useMemo(() => {
    const counts = availableDevices.reduce((acc, device) => {
      acc[device.dtype] = (acc[device.dtype] || 0) + 1;
      return acc;
    }, {});

    return localConfiguration.deviceTypes
      .map(type => ({
        ...type,
        count: counts[type.id] || 0,
      }))
      .filter(type => type.count > 0);
  }, [availableDevices, localConfiguration.deviceTypes]);

  // --- HANDLERS ---
  const handleFilterToggle = (category, id) => {
    setFilters(prev => {
      const currentSelection = prev[category];
      const newSelection = currentSelection.includes(id)
        ? currentSelection.filter(val => val !== id)
        : [...currentSelection, id];
      return { ...prev, [category]: newSelection };
    });
  };

  const handleDeviceSelectToggle = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };
  
  const handleSelectAllVisible = () => {
    const allVisibleIds = availableDevices.map(d => d.id);
    const areAllSelected = allVisibleIds.every(id => selectedDevices.includes(id));
    if (areAllSelected) {
      setSelectedDevices(prev => prev.filter(id => !allVisibleIds.includes(id)));
    } else {
      setSelectedDevices(prev => [...new Set([...prev, ...allVisibleIds])]);
    }
  };

  const handleSave = () => {
    onSave({playlist: selectedDevices}).then(() => {
      handleClose();
    });
  };

  const handleClose = () => {
    setFilters({ storeGroups: [], stores: [], deviceGroups: [], deviceTypes: [], searchTerm: '' });
    setSelectedDevices([]);
    onClose();
  };

  const allVisibleSelected = availableDevices.length > 0 && availableDevices.every(d => selectedDevices.includes(d.id));
  const someVisibleSelected = availableDevices.some(d => selectedDevices.includes(d.id));
  const hasActiveFilters = filters.storeGroups.length > 0 || filters.stores.length > 0 || filters.deviceGroups.length > 0 || filters.deviceTypes.length > 0;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: '85vh',
          bgcolor: 'background.paper',
          overflow: 'hidden'
        }
      }}
    >
      {/* HEADER FISSO */}
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
             <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                <MonitorIcon />
             </Box>
             <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Distribuzione Contenuti</Typography>
                <Typography variant="body2" color="text.secondary">Seleziona i dispositivi per {selectedCount} {selectedCount === 1 ? 'elemento' : 'elementi'}</Typography>
             </Box>
        </Stack>
        <IconButton onClick={handleClose} sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover', color: 'error.main' } }}>
            <CloseIcon />
        </IconButton>
      </Box>

      {/* BODY CON LAYOUT FLEXBOX (NO GRID PER IL LAYOUT PRINCIPALE) */}
      <DialogContent sx={{ p: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
          
          {/* --- SIDEBAR SX (Larghezza Fissa) --- */}
          <Box sx={{ 
              width: 280, 
              flexShrink: 0, // NON si restringe mai
              height: '100%',
              borderRight: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.default',
              display: 'flex', flexDirection: 'column'
          }}>
            <Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
               <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 1, textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                  <FilterIcon fontSize="inherit" sx={{ mr: 1 }}/>
                  Filtri Struttura
               </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, pt: 0 }}>
               {/* ACCORDIONS DEI FILTRI */}
               <FilterAccordion title="Gruppi Negozio" icon={<BusinessIcon />} defaultExpanded>
                  <List dense disablePadding>
                    {localConfiguration.storeGroups.map(group => (
                      <ListItem 
                        key={group.id} dense button onClick={() => handleFilterToggle('storeGroups', group.id)}
                        sx={{ borderRadius: 1.5, mb: 0.5, py: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                           <Checkbox 
                              edge="start" size="small" 
                              checked={filters.storeGroups.includes(group.id)} tabIndex={-1} disableRipple 
                              sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                           />
                        </ListItemIcon>
                        <ListItemText primary={group.description} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                      </ListItem>
                    ))}
                  </List>
               </FilterAccordion>

               <FilterAccordion title="Negozi Singoli" icon={<StorefrontIcon />}>
                  <List dense disablePadding>
                    {localConfiguration.stores.map(store => (
                      <ListItem key={store.id} dense button onClick={() => handleFilterToggle('stores', store.id)} sx={{ borderRadius: 1.5, mb: 0.5, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                           <Checkbox edge="start" size="small" checked={filters.stores.includes(store.id)} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={store.description} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                      </ListItem>
                    ))}
                  </List>
               </FilterAccordion>

               <Divider sx={{ my: 2, opacity: 0.5 }} />

               <FilterAccordion title="Gruppi Dispositivo" icon={<DnsIcon />}>
                  <List dense disablePadding>
                    {localConfiguration.deviceGroups.map(group => (
                      <ListItem key={group.id} dense button onClick={() => handleFilterToggle('deviceGroups', group.id)} sx={{ borderRadius: 1.5, mb: 0.5, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                           <Checkbox edge="start" size="small" checked={filters.deviceGroups.includes(group.id)} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={group.description} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                      </ListItem>
                    ))}
                  </List>
               </FilterAccordion>

               <FilterAccordion title="Tipi Dispositivo" icon={<WidgetsIcon />}>
                  <List dense disablePadding>
                    {localConfiguration.deviceTypes.map(type => (
                      <ListItem key={type.id} dense button onClick={() => handleFilterToggle('deviceTypes', type.id)} sx={{ borderRadius: 1.5, mb: 0.5, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                           <Checkbox edge="start" size="small" checked={filters.deviceTypes.includes(type.id)} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <Box component="span" sx={{ mr: 1, display: 'flex', color: 'text.secondary' }}>{React.cloneElement(type.icon, { fontSize: 'small' })}</Box>
                        <ListItemText primary={type.description} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                      </ListItem>
                    ))}
                  </List>
               </FilterAccordion>
            </Box>
          </Box>

          {/* --- MAIN STAGE DX (Flex Grow) --- */}
          <Box sx={{ 
              flexGrow: 1, 
              minWidth: 0, // CRUCIALE per permettere ai figli flex di restringersi
              display: 'flex', 
              flexDirection: 'column',
              bgcolor: 'background.paper'
          }}>
            
            {/* 1. TOP BAR (Search & Stats) */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
               <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                  
                  {/* Search Bar - Flex Grow */}
                  <Box sx={{ flexGrow: 1, width: '100%' }}>
                     <TextField 
                        fullWidth 
                        placeholder="Cerca dispositivo..." 
                        value={filters.searchTerm} 
                        onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))} 
                        InputProps={{ 
                            startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>),
                            sx: { borderRadius: 3, bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.default', '& fieldset': { borderColor: 'transparent' } }
                        }}
                        size="small"
                     />
                  </Box>

                  {/* Stats Cards - Flex Shrink 0 (Non collassano mai) */}
                  <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', maxWidth: '100%', flexShrink: 0, pb: 0.5 }}>
                      <StatCard icon={<MonitorIcon />} count={availableDevices.length} label="Totali" active={false} />
                      {deviceTypeBreakdown.map(type => (
                          <StatCard key={type.id} icon={type.icon} count={type.count} label={type.description} active={true} />
                      ))}
                  </Stack>
               </Stack>
               
               {/* Chip Filtri Attivi (Wrap) */}
               {hasActiveFilters && (
                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1, fontWeight: 700, color: 'text.secondary' }}>FILTRI ATTIVI:</Typography>
                    {filters.storeGroups.map(id => <Chip key={id} label={localConfiguration.storeGroups.find(g=>g.id===id)?.description} onDelete={() => handleFilterToggle('storeGroups', id)} size="small" sx={{ borderRadius: 1 }} />)}
                    {filters.stores.map(id => <Chip key={id} label={localConfiguration.stores.find(s=>s.id===id)?.description} onDelete={() => handleFilterToggle('stores', id)} size="small" sx={{ borderRadius: 1 }} />)}
                    {filters.deviceGroups.map(id => <Chip key={id} label={localConfiguration.deviceGroups.find(g=>g.id===id)?.description} onDelete={() => handleFilterToggle('deviceGroups', id)} size="small" sx={{ borderRadius: 1 }} />)}
                    {filters.deviceTypes.map(id => <Chip key={id} label={localConfiguration.deviceTypes.find(t=>t.id===id)?.description} onDelete={() => handleFilterToggle('deviceTypes', id)} size="small" sx={{ borderRadius: 1 }} icon={localConfiguration.deviceTypes.find(t=>t.id===id)?.icon} />)}
                  </Stack>
               )}
            </Box>

            {/* 2. SUB-HEADER: Select All */}
            <Box sx={{ px: 3, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                <FormControlLabel 
                    sx={{ ml: 0 }}
                    control={
                        <Checkbox 
                            size="small"
                            checked={allVisibleSelected} 
                            indeterminate={someVisibleSelected && !allVisibleSelected} 
                            onChange={handleSelectAllVisible} 
                            disabled={availableDevices.length === 0} 
                            sx={{ p: 0.5, mr: 1 }}
                        />
                    }
                    label={<Typography variant="body2" fontWeight={600} color="text.primary">Seleziona tutti i visibili</Typography>}
                />
                <Chip 
                    size="small" 
                    label={`${availableDevices.length} RISULTATI`} 
                    sx={{ fontWeight: 700, fontSize: '0.65rem', borderRadius: 1, height: 20 }} 
                />
            </Box>

            {/* 3. GRIGLIA RISULTATI (Scrollabile) */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: theme.palette.mode === 'light' ? '#ffffff' : 'inherit' }}>
                {availableDevices.length > 0 ? (
                  // Grid qui va bene perché è dentro un container che gestisce lo scroll
                  <Grid container spacing={2}>
                    {availableDevices.map(device => {
                        const storeName = localConfiguration.stores.find(s => s.id === device.store)?.description || 'Negozio non trovato';
                        const deviceIcon = localConfiguration.deviceTypes.find(t => t.id === device.dtype)?.icon || <MonitorIcon />;
                        const isSelected = selectedDevices.includes(device.id);

                        return (
                            <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} key={device.id}>
                                <Paper 
                                    variant="outlined" 
                                    onClick={() => handleDeviceSelectToggle(device.id)}
                                    sx={{ 
                                        p: 2, 
                                        cursor: 'pointer',
                                        position: 'relative',
                                        borderRadius: 3,
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        borderWidth: isSelected ? '2px' : '1px',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                                        boxShadow: isSelected ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}` : 'none',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            transform: 'translateY(-2px)',
                                            boxShadow: 2
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box 
                                            sx={{ 
                                                width: 44, height: 44, flexShrink: 0,
                                                borderRadius: '50%', 
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                bgcolor: isSelected ? 'primary.main' : alpha(theme.palette.action.active, 0.05),
                                                color: isSelected ? 'primary.contrastText' : 'text.secondary',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {isSelected ? <CheckCircleIcon /> : React.cloneElement(deviceIcon)}
                                        </Box>
                                        
                                        <Box sx={{ overflow: 'hidden', flex: 1 }}>
                                            <Typography variant="body2" fontWeight={700} noWrap sx={{ mb: 0.5 }}>{device.description}</Typography>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <StorefrontIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                <Typography variant="caption" color="text.secondary" noWrap fontWeight={500}>{storeName}</Typography>
                                            </Stack>
                                        </Box>
                                        
                                        {isSelected && (
                                            <Box sx={{ color: 'primary.main', flexShrink: 0 }}>
                                                <CheckCircleIcon fontSize="small" />
                                            </Box>
                                        )}
                                    </Stack>
                                </Paper>
                            </Grid>
                        );
                    })}
                  </Grid>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5, py: 10 }}>
                     <FilterIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
                     <Typography variant="h6" color="text.secondary">Nessun dispositivo trovato</Typography>
                     <Typography variant="body2" color="text.disabled">Prova a modificare i filtri di ricerca</Typography>
                  </Box>
                )}
            </Box>

          </Box>
      </DialogContent>

      <Divider />
      
      {/* FOOTER */}
      <DialogActions sx={{ p: 2, bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.default', justifyContent: 'space-between', flexShrink: 0 }}>
         <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
             <Badge badgeContent={selectedDevices.length} color="primary" showZero max={999}>
                <MonitorIcon color="action" />
             </Badge>
             <Typography variant="body2" fontWeight={600} color="text.primary">
                {selectedDevices.length} dispositivi selezionati
             </Typography>
         </Stack>

         <Stack direction="row" spacing={2}>
            <Button onClick={handleClose} sx={{ color: 'text.secondary', px: 2 }}>Annulla</Button>
            <Button 
                variant="contained" 
                onClick={handleSave} 
                disabled={selectedDevices.length === 0}
                startIcon={<DoneAllIcon />}
                disableElevation
                sx={{ px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
                Conferma Distribuzione
            </Button>
         </Stack>
      </DialogActions>
    </Dialog>
  );
}